import { X } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";

const curriculumData = [
  {
    id: "1",
    title: "자존감학과",
    description: "자기 가치 점검과 상처 회복을 통해 건강한 연애의 기초를 다집니다.",
    topics: ["자기 가치 발견", "과거 상처 치유", "자존감 회복 훈련"],
  },
  {
    id: "2",
    title: "소통기술학과",
    description: "호감형 대화법과 카톡/문자 훈련으로 소통 능력을 키웁니다.",
    topics: ["호감형 대화법", "카톡/문자 훈련", "경청과 공감 기술"],
  },
  {
    id: "3",
    title: "여성학과",
    description: "여성들을 위한 맞춤 연애 팁과 매력 발산 기술을 배웁니다.",
    topics: ["여성 매력 발견", "연애 심리 이해", "자신감 향상"],
  },
  {
    id: "4",
    title: "남성학과",
    description: "남성들을 위한 연애 팁과 매너, 리더십을 함양합니다.",
    topics: ["남성 매력 개발", "리더십과 매너", "감정 표현법"],
  },
  {
    id: "5",
    title: "연애학과",
    description: "데이트 전략과 연애 기술을 체계적으로 학습합니다.",
    topics: ["데이트 코스 기획", "연애 단계별 전략", "관계 발전 기술"],
  },
  {
    id: "6",
    title: "매력학과",
    description: "밀당, 유혹, 희소성 발산 기술로 매력을 극대화합니다.",
    topics: ["매력 포인트 발굴", "희소성 전략", "유혹의 기술"],
  },
  {
    id: "7",
    title: "사랑학과",
    description: "안정감 있는 관계 형성과 친밀감 훈련을 진행합니다.",
    topics: ["친밀감 형성", "신뢰 구축", "장기 관계 유지"],
  },
  {
    id: "8",
    title: "갈등학과",
    description: "싸움 후 회복과 위기관리 능력을 기릅니다.",
    topics: ["갈등 해결법", "화해의 기술", "위기 관리"],
  },
  {
    id: "9",
    title: "이별·회복학과",
    description: "실연 극복, 치유, 재도약을 위한 과정입니다.",
    topics: ["이별 극복", "마음 치유", "새 시작 준비"],
  },
  {
    id: "10",
    title: "결혼학과",
    description: "국제결혼, 초혼, 재혼을 위한 준비 과정입니다.",
    topics: ["결혼 준비", "배우자 선택", "재혼 설계"],
  },
  {
    id: "11",
    title: "중년성학과",
    description: "중년의 성 문제, 고민, 해결책을 함께 나눕니다.",
    topics: ["친밀감 회복", "소통 방법", "건강한 관계"],
  },
];

export const CurriculumModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center"
      data-testid="curriculum-modal-overlay"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div 
        className="relative bg-white rounded-2xl shadow-2xl w-[95%] max-w-4xl max-h-[90vh] overflow-hidden"
        data-testid="curriculum-modal-content"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-stone-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <span className="text-gold-500 font-medium text-xs tracking-wider uppercase block mb-1">
              Curriculum
            </span>
            <h2 className="font-sans text-2xl md:text-3xl font-[720] text-stone-800">
              연애사관학교
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-stone-100 rounded-full transition-colors"
            data-testid="curriculum-modal-close"
          >
            <X className="w-6 h-6 text-stone-600" />
          </button>
        </div>
        
        {/* Body */}
        <div className="overflow-y-auto max-h-[calc(90vh-100px)] p-6">
          <p className="text-stone-600 text-base mb-6 leading-relaxed">
            중년의 연애, 막막하셨죠?
            <br />
            New Coupling 연애사관학교에서 체계적으로 배우고 자신감 있게 사랑을 시작하세요.
          </p>
          
          {/* Stats */}
          <div className="flex items-center gap-4 mb-6 p-4 bg-burgundy-50 rounded-xl">
            <div className="w-14 h-14 rounded-full bg-burgundy-500 flex items-center justify-center">
              <span className="text-white font-sans font-[720] text-xl">11</span>
            </div>
            <div>
              <div className="text-sm text-stone-500">전문 과정</div>
              <div className="font-bold text-stone-800">학과 개설</div>
              <p className="text-sm text-stone-600 mt-1">
                체계적인 커리큘럼으로 연애의 모든 것을 배웁니다.
              </p>
            </div>
          </div>

          {/* Accordion */}
          <Accordion
            type="single"
            collapsible
            className="space-y-3"
            data-testid="curriculum-modal-accordion"
          >
            {curriculumData.map((item, index) => (
              <AccordionItem
                key={item.id}
                value={item.id}
                className="border border-stone-200 rounded-xl px-4 bg-stone-50/50 data-[state=open]:bg-white data-[state=open]:shadow-md transition-all duration-300"
                data-testid={`modal-curriculum-item-${index}`}
              >
                <AccordionTrigger
                  className="accordion-trigger-custom hover:no-underline py-4"
                  data-testid={`modal-curriculum-trigger-${index}`}
                >
                  <div className="flex items-center gap-3 text-left">
                    <div className="w-8 h-8 rounded-full bg-burgundy-500 text-white flex items-center justify-center text-sm font-bold">
                      {item.id}
                    </div>
                    <span className="font-semibold text-stone-800 text-sm md:text-base">
                      {item.title}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent
                  className="pb-4 pl-11"
                  data-testid={`modal-curriculum-content-${index}`}
                >
                  <p className="text-stone-600 text-sm mb-3">
                    {item.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {item.topics.map((topic) => (
                      <span
                        key={topic}
                        className="text-xs px-3 py-1 rounded-full bg-burgundy-50 text-burgundy-600"
                      >
                        {topic}
                      </span>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </div>
  );
};
